import { Info } from "lucide-react"

import { t } from "@/lib/i18n"

import { ConciliacionPage } from "@/routes/finance/ConciliacionPage"
import { ConciliacionBancariaPage } from "@/routes/finance/ConciliacionBancariaPage"
import { HojaVsLibroPage } from "@/routes/finance/HojaVsLibroPage"

/**
 * LOS DOS CUADRES, EN UNA SOLA PESTAÑA.
 *
 * Cesar, punto 8 de la cuarta tanda: «¿conciliación no es lo mismo que finanzas
 * vs contabilidad? Si son lo mismo unifica esas dos hojas».
 *
 * ===========================================================================
 * NO SON LO MISMO, Y POR ESO NO SE FUNDEN LOS DATOS — SE FUNDE EL SITIO
 * ===========================================================================
 *
 * Se miraron las dos antes de tocar nada, y comparan cosas distintas:
 *
 *   Conciliación            la hoja  ←→  lo FACTURADO   (`invoice`)
 *   Finanzas vs Contabilidad la hoja  ←→  el LIBRO       (los asientos)
 *
 * Son TRES fuentes y dos restas que comparten un lado. Fundirlas en una sola
 * tabla perdería una de las dos comparaciones, y cada una pide una acción
 * distinta: un descalce contra lo facturado se investiga o se importa; un
 * descalce contra el libro casi siempre es el calendario de carga.
 *
 * Pero la confusión de Cesar es legítima y la causa era de nombre: dos pestañas
 * seguidas, las dos comparando «la hoja contra algo», y ninguna de las dos
 * diciendo contra qué. «Conciliación» no nombra su otro lado y «Finanzas vs
 * Contabilidad» nombra secciones del menú, no fuentes.
 *
 * Así que se unifica **el sitio** —una pestaña, «Cuadres»— y cada mitad dice en
 * su encabezado contra qué cuadra. La diferencia deja de haber que adivinarla
 * desde dos nombres y se lee en la propia pantalla.
 *
 * ---------------------------------------------------------------------------
 * LAS DOS PÁGINAS SE MONTAN TAL CUAL, SIN COPIAR NADA
 *
 * `ConciliacionPage` y `HojaVsLibroPage` siguen siendo las dueñas de su lógica.
 * Esto es un contenedor: duplicar su contenido aquí habría hecho que el día que
 * una cambie, la copia se quede atrás — y entonces la misma cifra saldría
 * distinta según por dónde se entre, que es el defecto que esta casa persigue.
 */
export function CuadresPage() {
  return (
    <div className="space-y-5">
      <p className="flex items-start gap-1.5 rounded-md border border-dashed px-2.5 py-2 text-2xs leading-5 text-muted-foreground">
        <Info className="mt-px h-3 w-3 shrink-0" aria-hidden="true" />
        <span>
          {t("Tres cuadres. Dos contra tu hoja de gestión —contra lo que FACTURASTE y contra lo que está ASENTADO en el libro— y uno de los bancos contra el libro, movimiento a movimiento. No tienen por qué dar lo mismo, y cuando no cuadran piden cosas distintas.")}
        </span>
      </p>

      <section className="space-y-2">
        <div>
          <h2 className="text-sm font-medium leading-tight text-foreground">
            {t("Tu hoja contra lo que facturaste")}
          </h2>
          <p className="text-2xs leading-5 text-muted-foreground">
            {t("Devengado contra emitido. Una diferencia se investiga —hay ingreso sin factura y facturas que no son ingreso del período— o se importa, si lo que falta es carga.")}
          </p>
        </div>
        <ConciliacionPage />
      </section>

      <section className="space-y-2 border-t pt-5">
        <div>
          <h2 className="text-sm font-medium leading-tight text-foreground">
            {t("Tu hoja contra el libro contable")}
          </h2>
          <p className="text-2xs leading-5 text-muted-foreground">
            {t("Cuenta por cuenta, contra lo asentado. Mientras los comprobantes se carguen con retraso el libro va por detrás SIEMPRE, y eso es el calendario y no un descuadre: lo que importa es una cuenta que el libro tenga y tu hoja no.")}
          </p>
        </div>
        <HojaVsLibroPage />
      </section>

      <section className="space-y-2 border-t pt-5">
        <div>
          <h2 className="text-sm font-medium leading-tight text-foreground">
            {t("Los bancos contra el libro, movimiento a movimiento")}
          </h2>
          <p className="text-2xs leading-5 text-muted-foreground">
            {t("Cada movimiento del extracto con la factura o el comprobante que lo explica. Marty concilia solo lo inequívoco al cargar el extracto; lo demás lo confirma una persona. Lo que queda sin pareja es la lista de trabajo.")}
          </p>
        </div>
        <ConciliacionBancariaPage />
      </section>
    </div>
  )
}
